/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

//utils/uiRulesHelper.js

/**
 * uiRulesHelper
 * -------------
 * Centralized helper functions for evaluating `ui_rules` on table fields.
 *
 * This module is used by DynamicCreateEditForm (and can be reused elsewhere)
 * to decide:
 *  - whether a field should be visible for the current user
 *  - whether a field should be readonly for the current user
 *
 * It encapsulates the logic around:
 *  - `visible`, `visible_if.role_in`, `visible_if.value_equals`
 *  - `readonly`, `readonly_if.role_in`, `readonly_if.value_equals`
 *  - user roles passed from the application
 *
 * All functions are pure and easy to test.
 */

/**
 * Safely extract the `ui_rules` object from a field definition.
 *
 * @param {Object} field - Field metadata object from the backend.
 * @returns {Object} ui_rules object, or an empty object if none is defined.
 */
export function getUiRules(field) {
    return field && field.ui_rules ? field.ui_rules : {};
}

/**
 * Returns true if the current user has at least one of the required roles.
 *
 * @param {string[]} currentUserRoles - Roles assigned to the current user.
 * @param {string[]} requiredRoles - Roles required by this rule.
 * @returns {boolean} True if user has at least one required role.
 */
export function userHasAnyRole(currentUserRoles, requiredRoles) {
    if (!Array.isArray(requiredRoles) || requiredRoles.length === 0) return true;
    if (!Array.isArray(currentUserRoles) || currentUserRoles.length === 0)
        return false;
    return currentUserRoles.some((r) => requiredRoles.includes(r));
}

/**
 * Evaluate a value_equals rule against the current form state.
 *
 * uiRuleValueEquals example shapes:
 *  - { status: "APPROVED" }
 *  - { status: ["APPROVED", "REJECTED"] }
 *
 * For each key in value_equals:
 *  - read `formState[fieldName]`
 *  - if expected is an array → current value must be included in that array
 *  - if expected is scalar → current value must strictly equal that value
 *
 * If all conditions pass, the rule is satisfied and we return true.
 * If ANY condition fails, we return false.
 *
 * @param {Object} uiRuleValueEquals - value_equals object from ui_rules.
 * @param {Object} formState - Current form state keyed by field name.
 * @returns {boolean} True if all value_equals conditions are satisfied.
 */
export function matchesValueEquals(uiRuleValueEquals, formState) {
    if (!uiRuleValueEquals || typeof uiRuleValueEquals !== "object") {
        return true; // nothing to check
    }

    const entries = Object.entries(uiRuleValueEquals);
    if (entries.length === 0) return true;

    for (const [fieldName, expected] of entries) {
        const current = formState ? formState[fieldName] : undefined;

        // If expected is an array → require current to be one of the elements
        if (Array.isArray(expected)) {
            if (!expected.includes(current)) {
                return false;
            }
        } else {
            // Scalar comparison (string | number | boolean)
            if (current !== expected) {
                return false;
            }
        }
    }

    return true;
}

/**
 * Determine if a field should be visible for the current user and form state.
 *
 * Applies:
 *  - ui_rules.visible
 *  - ui_rules.visible_if.role_in
 *  - ui_rules.visible_if.value_equals
 *
 * @param {Object} field - Field metadata (includes ui_rules).
 * @param {Object} formState - Current form values keyed by field name.
 * @param {string[]} currentUserRoles - Roles assigned to current user.
 * @returns {boolean} True if field should be visible, false to hide.
 */
export function isFieldVisible(field, formState, currentUserRoles) {
    const ui = getUiRules(field);

    // 1. Explicit visibility flag
    if (ui.visible === false) {
        return false;
    }

    const vIf = ui.visible_if || {};

    // 2. Role-based visibility
    if (Array.isArray(vIf.role_in) && vIf.role_in.length > 0) {
        if (!userHasAnyRole(currentUserRoles, vIf.role_in)) {
            return false;
        }
    }

    // 3. value_equals visibility condition
    //    If present, all conditions must be satisfied.
    if (vIf.value_equals) {
        const ok = matchesValueEquals(vIf.value_equals, formState);
        if (!ok) return false;
    }

    return true;
}

/**
 * Determine if a field should be readonly for the current user and form state.
 *
 * Applies:
 *  - ui_rules.readonly
 *  - ui_rules.readonly_if.role_in
 *  - ui_rules.readonly_if.value_equals
 *
 * @param {Object} field - Field metadata (includes ui_rules).
 * @param {Object} formState - Current form values keyed by field name.
 * @param {string[]} currentUserRoles - Roles assigned to current user.
 * @returns {boolean} True if field should be readonly, false if editable.
 */
export function isFieldReadOnly(field, formState, currentUserRoles) {
    const ui = getUiRules(field);

    // 1. Explicit readonly flag
    if (ui.readonly === true) {
        return true;
    }

    const rIf = ui.readonly_if || {};

    // 2. Role-based readonly
    if (Array.isArray(rIf.role_in) && rIf.role_in.length > 0) {
        if (userHasAnyRole(currentUserRoles, rIf.role_in)) {
            return true;
        }
    }

    // 3. value_equals readonly condition
    //    If any value_equals condition matches, mark as readonly.
    if (rIf.value_equals) {
        const ok = matchesValueEquals(rIf.value_equals, formState);
        if (ok) return true;
    }

    return false;
}


/**
 * Compute the initial value for a field when building form state.
 *
 * Rules:
 *  - In update mode:
 *      - If `existingValue` is defined, always use it (do NOT apply static defaults).
 *  - In create mode:
 *      - If ui_rules.default is defined, use that as initial value.
 *      - Otherwise, return the usual empty baseline for that data_type.
 *
 * Special handling:
 *  - Boolean:
 *      - Accepts true/false (boolean) or "true"/"false" (string) for defaults.
 *      - Anything else becomes null.
 *
 * @param {Object} field
 *    Field metadata (must have data_type, name, and may have ui_rules).
 * @param {any} existingValue
 *    Value coming from an existing record (for update mode), or undefined/null in create mode.
 * @param {"create"|"update"} mode
 *    Effective form mode.
 * @returns {any} The initial value to put into formData[field.name].
 */
export function getInitialFieldValue(field, existingValue, mode) {
    const type = (field.data_type || "").toLowerCase();
    const ui = field.ui_rules || {};
    const hasStaticDefault = Object.prototype.hasOwnProperty.call(ui, "default");
    const staticDefault = ui.default;

    // UPDATE MODE: always prefer existing value; do not apply defaults
    if (mode === "update" && existingValue !== undefined) {
        if (type === "boolean") {
            // Normalize booleans to true/false
            return !!existingValue;
        }
        return existingValue;
    }

    // CREATE MODE: no existing value → try ui_rules.default first
    if (mode === "create" && hasStaticDefault) {
        if (type === "boolean") {
            if (staticDefault === true || staticDefault === false) {
                return staticDefault;
            }
            if (staticDefault === "true") return true;
            if (staticDefault === "false") return false;
            return null;
        }
        // For other types, keep as-is (conversion happens later in handleSubmit)
        return staticDefault;
    }

    // Baseline defaults when no static default is defined
    if (type === "boolean") return null;
    return "";
}

/**
 * Returns the placeholder to use for a field.
 * Priority:
 *  1) ui_rules.widget.placeholder (if present and non-empty)
 *  2) f.hint (backend hint)
 *  3) empty string
 */
export function getFieldPlaceholder(f) {
    const ui = f.ui_rules || {};
    const widget = ui.widget || {};
    if (widget.placeholder && typeof widget.placeholder === "string") {
        return widget.placeholder;
    }
    if (f.hint && typeof f.hint === "string") {
        return f.hint;
    }
    return "";
}

/**
 * Returns the hint text for a field for a specific role.
 *
 * Priority:
 *  1) field.ui_rules.hint_by_role[roleName] (trimmed, if non-empty)
 *  2) null when no role-specific hint is defined
 *
 * @param {Object} field - Field metadata, may contain ui_rules.hint_by_role.
 * @param {string|null} roleName - Current role name (e.g. "Staff") or null.
 * @returns {string|null}
 */
export function getFieldHintForRole(field, roleName) {
    if (!field || !roleName) return null;

    const ui = field.ui_rules || {};
    const hintByRole = ui.hint_by_role || {};
    const raw = hintByRole[roleName];

    if (typeof raw !== "string") return null;

    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : null;
}


/**
 * Returns the effective static options for a field for the given role.
 *
 * Reads:
 *  - field.ui_rules.options_source.values  (global list)
 *  - field.ui_rules.options_by_role.per_role[roleName]  (role override)
 *
 * Rules:
 *  - If options_source.type !== "static" → return empty array.
 *  - If a non-empty per-role list exists for roleName → return that.
 *  - Else return the global values list or [].
 *
 * Each item is normalized to { label, value } with both as strings.
 */
export function getFieldOptionsForRole(field, roleName) {
    if (!field) return [];

    const ui = field.ui_rules || {};
    const optSrc = ui.options_source || null;

    // Decide if options are enabled:
    // - Case 1: options_source.type === "static"  → use static + per_role
    // - Case 2: options_source is missing BUT options_by_role.per_role exists → still use per_role
    const hasStaticSource = optSrc && optSrc.type === "static";

    const perRoleWrapper = (ui.options_by_role || {}).per_role || {};
    const roleListRaw = roleName ? perRoleWrapper[roleName] : null;
    const roleList = Array.isArray(roleListRaw) ? roleListRaw : [];

    // If neither static source nor per_role options exist, nothing to do
    if (!hasStaticSource && roleList.length === 0) {
        return [];
    }

    const globalValues = hasStaticSource && Array.isArray(optSrc.values)
        ? optSrc.values
        : [];

    const baseList =
        roleName && roleList.length > 0
            ? roleList         // override for that role
            : globalValues;    // fall back to global (may be empty)

    // Normalize items to {label, value} with strings
    return baseList.map((opt) => {
        if (opt && typeof opt === "object") {
            return {
                label: String(opt.label ?? opt.value ?? ""),
                value: String(opt.value ?? opt.label ?? ""),
            };
        }
        return { label: String(opt), value: String(opt) };
    });
}

